/**
 * Korean render vocabulary.
 *
 * Self-registering: importing this module makes the words available to
 * `render(node, 'ko')`. Kept separate from the profile so a parse-only
 * bundle can drop it — see lexicon-registry.ts for why that separation exists.
 */
import type { LanguageLexicon } from '../generators/profiles/types';
import { registerLexicon } from '../lexicon-registry';

export const koreanLexicon: LanguageLexicon = {
  events: {
    blur: { primary: '블러' },
    change: { primary: '변경' },
    click: { primary: '클릭' },
    dblclick: { primary: '더블클릭' },
    focus: { primary: '포커스' },
    input: { primary: '입력' },
    keydown: { primary: '키다운' },
    keypress: { primary: '키프레스' },
    keyup: { primary: '키업' },
    load: { primary: '로드' },
    mousedown: { primary: '마우스다운' },
    mouseenter: { primary: '마우스엔터' },
    mouseleave: { primary: '마우스리브' },
    mousemove: { primary: '마우스무브' },
    mouseout: { primary: '마우스아웃' },
    mouseover: { primary: '마우스오버' },
    mouseup: { primary: '마우스업' },
    reset: { primary: '재설정' },
    resize: { primary: '리사이즈' },
    scroll: { primary: '스크롤' },
    submit: { primary: '제출' },
    touchcancel: { primary: '터치취소' },
    touchend: { primary: '터치종료' },
    touchmove: { primary: 'touchmove' },
    touchstart: { primary: 'touchstart' },
    unload: { primary: 'unload' },
  },
  logical: {
    and: { primary: '그리고' },
    bind: { primary: '바인드' },
    changes: { primary: '변경되면' },
    contains: { primary: '포함' },
    else: { primary: '아니면' },
    end: { primary: '끝' },
    equals: { primary: '같다' },
    exists: { primary: '존재' },
    has: { primary: '있다' },
    have: { primary: '있다' },
    includes: { primary: '포함하다' },
    is: { primary: '이다' },
    live: { primary: '라이브' },
    matches: { primary: '일치' },
    not: { primary: '아니' },
    or: { primary: '또는' },
    otherwise: { primary: '그렇지않으면' },
    then: { primary: '그러면' },
    when: { primary: '때' },
    where: { primary: '어디' },
  },
  temporal: {
    forever: { primary: '영원히' },
    h: { primary: '시' },
    hour: { primary: '시간' },
    hours: { primary: '시간' },
    millisecond: { primary: '밀리초' },
    milliseconds: { primary: '밀리초' },
    min: { primary: '분' },
    minute: { primary: '분' },
    minutes: { primary: '분' },
    ms: { primary: 'ms' },
    s: { primary: '초' },
    second: { primary: '초' },
    seconds: { primary: '초' },
    times: { primary: '번' },
  },
  values: {
    body: { primary: '바디' },
    detail: { primary: '세부' },
    document: { primary: '문서' },
    element: { primary: '요소' },
    event: { primary: '이벤트' },
    false: { primary: '거짓' },
    it: { primary: '그것' },
    its: { primary: '그것의' },
    me: { primary: '나' },
    my: { primary: '내' },
    myself: { primary: '나자신' },
    null: { primary: '널' },
    result: { primary: '결과' },
    target: { primary: '대상' },
    true: { primary: '참' },
    undefined: { primary: '정의안됨' },
    value: { primary: '값' },
    window: { primary: '창' },
    you: { primary: '너' },
    your: { primary: '네' },
    yourself: { primary: '너자신' },
  },
  attributes: {
    attribute: { primary: '속성' },
    attributes: { primary: '속성들' },
    class: { primary: '클래스' },
    classes: { primary: '클래스들' },
    properties: { primary: '프로퍼티들' },
    property: { primary: '프로퍼티' },
    style: { primary: '스타일' },
    styles: { primary: '스타일들' },
  },
  expressions: {
    up: { primary: '위쪽' },
    down: { primary: '아래쪽' },
    left: { primary: '왼쪽' },
    right: { primary: '오른쪽' },
    at: { primary: '에서' },
    back: { primary: '뒤로' },
    characters: { primary: '문자' },
    children: { primary: '자식' },
    closest: { primary: '가장가까운' },
    elsewhere: { primary: '바깥' },
    empty: { primary: '비어있는' },
    'ends with': { primary: '로끝나는' },
    exclusive: { primary: '제외' },
    first: { primary: '첫번째' },
    'ignoring case': { primary: '대소문자_무시' },
    inclusive: { primary: '포함' },
    index: { primary: '인덱스' },
    'joined by': { primary: '로_결합' },
    last: { primary: '마지막' },
    'mapped to': { primary: '로_변환' },
    next: { primary: '다음' },
    no: { primary: '없음' },
    parent: { primary: '부모' },
    prev: { primary: '이전' },
    previous: { primary: '이전' },
    random: { primary: '무작위' },
    some: { primary: '일부' },
    'sorted by': { primary: '로_정렬' },
    'split by': { primary: '로_분할' },
    start: { primary: '시작' },
    catch: { primary: '잡다' },
    finally: { primary: '결국' },
    def: { primary: '함수' },
    'starts with': { primary: '로시작' },
    within: { primary: '이내' },
  },
};

registerLexicon('ko', koreanLexicon);
